<script setup>
import LoadingArea from "@/components/utils/LoadingArea.vue";
import ViewSchemePlanPropertiesTable from "./ViewSchemePlanPropertiesTable.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import Pagination from "@/components/utils/Pagination.vue";
import SchemePlanPropertyDetails from "./SchemePlanPropertyDetails.vue";
import CreateSchemePlanPropertyForm from "./CreateSchemePlanPropertyForm.vue";
import { useFetchSchemePlanProperties } from "./scheme_plan_business_logic";
import { computed, ref } from "vue";

const {
  propertyData,
  searchModel,
  fetchProperties,
} = useFetchSchemePlanProperties();

// Page navigation
const pages = {
  VIEW_PROPERTIES_TABLE: 0,
  VIEW_PROPERTY: 1,
};
const currentPage = ref(pages.VIEW_PROPERTIES_TABLE);
const goTo = {
  viewPropertiesTable() {
    currentPage.value = pages.VIEW_PROPERTIES_TABLE;
  },
  viewProperty(property) {
    propertyToView.value = property;
    currentPage.value = pages.VIEW_PROPERTY;
  },
};

const propertyToView = ref(null);
const propertyToViewNotEmpty = computed(() => {
  return !!propertyToView.value;
});

function onPaginationClicked(newPage) {
  propertyData.setNewPage(newPage);
}

function onViewPropertyClicked(property) {
  goTo.viewProperty(property);
}

// Create dialog
const showCreatePropertyDialog = ref(false);

function onAddPropertyClicked() {
  showCreatePropertyDialog.value = true;
}

function onPropertySaved(response) {
  showCreatePropertyDialog.value = false;
  fetchProperties();
  snackbarMessage.value = "Scheme plan property saved successfully! PN: " + (response?.pn || "");
  snackbarColor.value = "success";
  snackbar.value = true;
}

function onPropertySaveError(error) {
  snackbarMessage.value =
    error?.response?.data?.errors
      ? Object.values(error.response.data.errors).flat()[0]
      : 'An error occurred. Please try again.';
  snackbarColor.value = "error";
  snackbar.value = true;
}

// Snackbar
const snackbar = ref(false);
const snackbarMessage = ref("");
const snackbarColor = ref("");
</script>

<template>
  <v-container fluid class="pa-0">
    <!-- Main Content with Tabs -->
    <v-tabs-items v-model="currentPage">
      <!-- Tab 0: Properties Table -->
      <v-tab-item :value="pages.VIEW_PROPERTIES_TABLE">
        <v-row>
          <v-col cols="12">
            <div class="text-h6 text-center font-weight-bold primary--text">
              My Scheme Plan Properties
            </div>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col cols="12" md="6">
            <LabelledField label="Search">
              <v-text-field
                class="background text-field"
                outlined
                dense
                v-model="searchModel"
                clearable
                placeholder="Search by PN or LR number..."
              />
            </LabelledField>
          </v-col>
          <v-col cols="12" md="auto" class="d-flex mt-4 align-center">
            <v-btn
              color="primary"
              depressed
              class="text-none"
              @click="onAddPropertyClicked"
            >
              <v-icon left>mdi-plus</v-icon>
              New Property
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <LoadingArea :loading="propertyData.fetchingData">
              <v-container fluid class="pa-0">
                <v-row>
                  <v-col cols="12">
                    <ViewSchemePlanPropertiesTable
                      :properties="propertyData.fetchedData"
                      :propertiesNotEmpty="propertyData.fetchedDataNotEmpty"
                      :loading="propertyData.fetchingData"
                      @viewPropertyClicked="onViewPropertyClicked($event)"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </LoadingArea>
          </v-col>
        </v-row>

        <!-- Create Property Dialog -->
        <v-dialog
          v-model="showCreatePropertyDialog"
          max-width="1000px"
          persistent
        >
          <CreateSchemePlanPropertyForm
            v-if="showCreatePropertyDialog"
            @close="showCreatePropertyDialog = false"
            @save-success="onPropertySaved"
            @save-error="onPropertySaveError"
          />
        </v-dialog>

        <v-row justify="end">
          <v-col cols="auto">
            <Pagination
              :totalNumberOfItems="propertyData.paginationData.total"
              :itemsPerPage="propertyData.paginationData.perPage"
              :currentPage="propertyData.paginationData.currentPage"
              @paginationClicked="onPaginationClicked"
            />
          </v-col>
        </v-row>
      </v-tab-item>

      <!-- Tab 1: Property Detail -->
      <v-tab-item :value="pages.VIEW_PROPERTY">
        <v-row>
          <v-col cols="12">
            <SchemePlanPropertyDetails
              v-if="propertyToViewNotEmpty"
              :property="propertyToView"
              showGoBack
              @goBackClicked="goTo.viewPropertiesTable"
            />
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      timeout="4000"
      top
      app
      style="z-index: 10000"
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>
